// Вход и сеанс (contracts/api.md, «Вход и сеанс»).
import { z } from '../kernel/zod.ts';
import { AreaSchema } from '../access/area.ts';
import { UserSchema } from '../access/user.ts';
import { ExternalAccessModeSchema } from '../access/email-policy.ts';
import { DateSchema, PersonRefSchema } from '../kernel/common.ts';
import { EMAIL_MAX, isEmailAddress } from '../kernel/email.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { OperationSchema, ScopeRuleSchema } from '../rights/matrix.ts';
import { MePersonSchema } from '../auth/link.ts';
import { RoleSchema } from '../rights/roles.ts';

/** Длина пароля во входящих данных ограничена, чтобы хеширование не стало нагрузкой. */
export const PASSWORD_MAX_LENGTH = 1024;

const password = (message: string) =>
  z
    .string({ error: message })
    .min(1, { error: message })
    .max(PASSWORD_MAX_LENGTH, { error: message });

/**
 * POST /api/auth/login. Почта не проверяется на формат: ответ на любую неудачу —
 * одинаковый AUTH-001 (FR-001б); блокировка считается по введённой почте.
 */
export const LoginBodySchema = z.object({
  email: z
    .string({ error: fieldMessages.loginEmail })
    .trim()
    .min(1, { error: fieldMessages.loginEmail })
    .max(254, { error: fieldMessages.loginEmail }),
  password: password(fieldMessages.password),
});
export type LoginBody = z.input<typeof LoginBodySchema>;

/**
 * POST /api/auth/password-recovery (025, contracts/api.md; research Р1): почта проверяется
 * по формату — ошибка формата не зависит от учётной записи. Ответ одинаков для любой почты.
 */
export const PasswordRecoveryBodySchema = z.object({
  email: z
    .string({ error: fieldMessages.loginEmail })
    .trim()
    // Пустая почта — один текст «Введите почту», без второго о формате.
    .min(1, { error: fieldMessages.loginEmail, abort: true })
    .max(EMAIL_MAX, { error: fieldMessages.recoveryEmail })
    .refine(isEmailAddress, { error: fieldMessages.recoveryEmail }),
});
export type PasswordRecoveryBody = z.input<typeof PasswordRecoveryBodySchema>;

/** 202 на запрос восстановления: срок ссылки из письма, часов (auth.recovery.linkHours). */
export const PasswordRecoveryResponseSchema = z.object({
  linkHours: z.number().int().positive(),
});
export type PasswordRecoveryResponse = z.infer<typeof PasswordRecoveryResponseSchema>;

/** POST /api/auth/password: правила нового пароля проверяет сервер (AUTH-007). */
export const ChangePasswordBodySchema = z.object({
  currentPassword: password(fieldMessages.currentPassword),
  newPassword: password(fieldMessages.newPassword),
});
export type ChangePasswordBody = z.input<typeof ChangePasswordBodySchema>;

/**
 * Значения защиты входа из настроек (принцип VIII; до раздела настроек 003 — из конфигурации
 * сервера). Тексты и проверки интерфейса берут числа отсюда, а не зашивают их.
 */
export const AuthSettingsSchema = z.object({
  /** Пароль — не короче стольких знаков (FR-004; auth.password.minLength). */
  minLength: z.number().int().positive(),
  /** Неудачных попыток подряд до блокировки входа (FR-005; auth.lockout.maxFailures). */
  maxFailures: z.number().int().positive(),
  /** На сколько минут блокируется вход (FR-005; auth.lockout.minutes). */
  lockMinutes: z.number().int().positive(),
});
export type AuthSettings = z.infer<typeof AuthSettingsSchema>;

/** Действующая роль пользователя на момент запроса. */
export const MeRoleSchema = z.object({
  role: RoleSchema,
  areas: z.array(AreaSchema),
  /** Последний день назначения; у замещения — для пометки «Замещаете: {роль} до {дата}». */
  validTo: DateSchema.optional(),
  /** Чью роль замещает. */
  substitutesFor: PersonRefSchema.optional(),
});
export type MeRole = z.infer<typeof MeRoleSchema>;

/** GET /api/auth/me. */
export const MeResponseSchema = z.object({
  user: UserSchema,
  roles: z.array(MeRoleSchema),
  /** Разрешённые операции и самое широкое правило — effectivePermissions(). */
  permissions: z.partialRecord(OperationSchema, ScopeRuleSchema),
  /** Число действующих сеансов пользователя (FR-009). */
  sessionCount: z.number().int().nonnegative(),
  /** Активные администраторы клиента — к кому обратиться при отказе (FR-016). */
  admins: z.array(z.object({ fullName: z.string() })),
  build: z.object({ version: z.string(), commit: z.string() }),
  /** Часовой пояс установки: Asia/Yekaterinburg. */
  timeZone: z.string(),
  /** Корпоративные домены (FR-001в) — для примера в поле почты «morozov@{домен}». */
  domains: z.array(z.string()),
  /** 022: вошёл внешний пользователь — права только из ролей в протоколах (research, Р3). */
  external: z.boolean(),
  /**
   * 022: настройка «Внешний доступ к протоколам» — для формы приглашения и «Компании» только
   * для чтения. Только тем, у кого есть minutes.access.manage; остальным — null (решение 3 плана).
   */
  externalAccess: z
    .object({ mode: ExternalAccessModeSchema, domains: z.array(z.string()) })
    .nullable(),
  /** Значения защиты входа: длина пароля, число попыток и минуты блокировки. */
  settings: AuthSettingsSchema,
  /**
   * 030 (FR-001): человек справочника, с которым связана учётная запись, — «Вы вошли как …»,
   * «Мои задачи» в меню; нет связи — null.
   */
  person: MePersonSchema.nullable(),
  /** 030 (FR-031, ADR-0044): сеанс по ссылке — узкий каркас: «Мои задачи», «Помощь», «Выйти». */
  linkSession: z.boolean(),
  /**
   * 031 (FR-028): пояс пользователя для писем — из браузера при первом входе; нет — null (веб
   * отправляет пояс браузера один раз, POST /api/auth/time-zone).
   */
  userTimeZone: z.string().nullable().optional(),
  /**
   * 032 (FR-021, поправка XII 2.7.0): человек пользователя — основной куратор действующего
   * подразделения или организации: в меню — «Просрочено у ваших сотрудников».
   */
  team: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
